<template>
  <a-config-provider
    :locale="antdLocale"
    :theme="antdTheme"
    :get-popup-container="getPopupContainer"
  >
    <router-view />
  </a-config-provider>
</template>

<script setup>
import { computed, watch } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import { useI18n } from 'vue-i18n';
import { theme } from 'ant-design-vue';
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import enUS from 'ant-design-vue/es/locale/en_US';
import zhTW from 'ant-design-vue/es/locale/zh_TW';
import jaJP from 'ant-design-vue/es/locale/ja_JP';
import { useTheme } from '@sys/shared/composables';
import { SIZES, TYPOGRAPHY } from '@/styles/tokens';

const { locale } = useI18n();
const { schemeName, SCHEME_META, THEME_PRESETS, THEME_LAYOUT } = useTheme();

// 所有 antd Portal 弹窗强制挂载到 #app 内，保证全局 zoom 时定位正确
function getPopupContainer() {
  return document.getElementById('app');
}

const antdLocale = computed(() => {
  const localeMap = {
    'en-US': enUS,
    'zh-TW': zhTW,
    ja: jaJP,
    'zh-CN': zhCN,
  };
  return localeMap[locale.value] || zhCN;
});

const antdTheme = computed(() => {
  const preset = THEME_PRESETS[schemeName.value];
  const layout = THEME_LAYOUT[schemeName.value];
  if (!preset) {
    return { algorithm: theme.defaultAlgorithm, token: {} };
  }

  const isDeep = SCHEME_META[schemeName.value]?.deep;
  return {
    algorithm: isDeep ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: preset.primary,
      colorPrimaryHover: preset.primaryHover,
      colorPrimaryActive: preset.primaryHover,
      colorText: preset.textPrimary,
      colorTextSecondary: preset.textSecondary,
      colorTextTertiary: preset.textTertiary,
      colorBorder: preset.borderDefault,
      colorBorderSecondary: preset.borderLight,
      colorBgLayout: preset.bgPage,
      colorBgContainer: preset.bgCard,
      fontFamily: TYPOGRAPHY.fontFamily,
      fontSize: TYPOGRAPHY.fontSizeBase,
      fontSizeSM: TYPOGRAPHY.fontSizeSm,
      fontSizeLG: TYPOGRAPHY.fontSizeLg,
      fontSizeXL: TYPOGRAPHY.fontSizeXl,
      lineHeight: TYPOGRAPHY.lineHeightBase,
      lineHeightSM: TYPOGRAPHY.lineHeightTight,
      lineHeightLG: TYPOGRAPHY.lineHeightLoose,
      fontWeightStrong: TYPOGRAPHY.fontWeightSemibold,
    },
    components: {
      Layout: {
        headerBg: preset.headerBg,
        siderBg: preset.siderBg,
      },
      Modal: {
        contentBg: preset.modalContentBg,
        headerBg: preset.modalHeaderBg,
        titleColor: preset.modalTitleColor,
        titleFontSize: TYPOGRAPHY.fontSizeLg,
        titleLineHeight: 1,
        footerBg: preset.modalFooterBg,
        borderRadiusLG: layout ? parseInt(layout.radiusLg) : 16,
        paddingMD: 14,
        paddingContentHorizontalLG: 24,
        marginXS: 16,
      },

      Form: {
        margin: 24,
      },
      Menu: {
        itemColor: preset.siderText,
        itemHoverColor: preset.siderTextHover,
        itemSelectedColor: preset.menuSelectedText,
        itemBg: preset.siderItemBg,
        itemHoverBg: preset.siderBgHover,
        itemSelectedBg: preset.siderBgActive || preset.primaryLight,
        iconSize: 14,
        itemHeight: 42,
        itemMarginInline: SIZES.paddingSm,
        itemMarginBlock: 4,
        itemBorderRadius: SIZES.controlRadiusLg,
        iconMarginInline: SIZES.paddingSm,
        iconColor: preset.siderIcon,
        iconHoverColor: preset.siderIconHover,
        iconActivatedColor: preset.siderIconActive,
        fontSize: TYPOGRAPHY.fontSizeBase,
      },
      Card: {
        borderRadius: SIZES.cardRadius,
        paddingLG: SIZES.paddingLg,
      },
      Table: {
        borderRadius: SIZES.tableRadius,
        headerBg: preset.tableHeaderBg || preset.bgPlaceholder,
        headerColor: preset.textHeading,
        headerBorderRadius: SIZES.tableRadius,
        rowHoverBg: preset.bgHover,
        padding: SIZES.paddingMd,
        cellPaddingBlock: SIZES.paddingMd,
        cellFontSize: TYPOGRAPHY.fontSizeSm,
        headerFontSize: TYPOGRAPHY.fontSizeSm,
      },
      Tag: { borderRadius: SIZES.controlRadiusLg },
      Pagination: {
        borderRadius: SIZES.controlRadiusLg,
        itemActiveBg: preset.primaryLight,
        itemActiveColor: preset.primary,
      },
      Button: {
        borderRadius: SIZES.controlRadius,
        controlHeight: SIZES.controlHeight,
        controlHeightLG: SIZES.controlHeightLg,
        controlHeightSM: SIZES.controlHeightSm,
        paddingInline: SIZES.paddingMd,
        paddingInlineLG: SIZES.paddingLg,
        paddingInlineSM: SIZES.paddingSm,
        fontWeight: TYPOGRAPHY.fontWeightMedium,
        colorBorder: preset.borderColor,
      },
      Input: {
        controlHeight: SIZES.controlHeight,
        borderRadius: SIZES.controlRadius,
        paddingInline: SIZES.paddingSm,
        paddingBlock: 0,
        fontSize: TYPOGRAPHY.fontSizeBase,
      },
      Steps: {
        colorPrimary: preset.primary,
        colorTextDisabled: preset.textDisabled,
      },
      Select: {
        borderRadius: SIZES.controlRadius,
        controlHeight: SIZES.controlHeight,
        colorBorder: preset.borderDefault,
      },
      DatePicker: {
        borderRadius: SIZES.controlRadius,
        controlHeight: SIZES.controlHeight,
        colorBorder: preset.borderDefault,
      },
      Tabs: {
        itemActiveColor: preset.primary,
        itemHoverColor: preset.primaryHover,
        itemSelectedColor: preset.primary,
      },
    },
  };
});

// 同步 Day.js locale 到 Ant Design Vue locale 的映射
const dayjsLocaleMap = {
  'zh-CN': 'zh-cn',
  'zh-TW': 'zh-tw',
  'en-US': 'en',
  ja: 'ja',
};
watch(
  locale,
  val => {
    const dayjsLocale = dayjsLocaleMap[val];
    if (dayjsLocale) {
      dayjs.locale(dayjsLocale);
    }
  },
  { immediate: true }
);
</script>
